import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTO, SERVER, setServer } from '../config';
import { IS_SHELL, SHELL } from '../shell';
import { PasswordField } from './PasswordField';
import { RecentCodeField } from './RecentCodeField';
import { EmailSuggestField } from './EmailSuggestField';
import { loadLastLoginId } from '../appShell/loginMemory';
import { requestPasswordReset } from '../api';
import { useMyConnection } from './myConnection/useMyConnection';
import { MyDeviceInfo } from './myConnection/MyDeviceInfo';
import { Drawer } from '../ui/Drawer';
import { applyMainLayout, MAIN_LAYOUT } from '../appShell/mainWindow';
import { SettingsPage } from './myConnection/SettingsPage';

/**
 * **첫 화면(로그인 전)** — 2026-09-27 개편(`RSP 화면.pdf`, 사용자 결정).
 *
 *   머리줄  : "RemoteSupport v0.6.7" · 오른쪽 ≡ (오른쪽 서랍 메뉴)
 *   가운데 카드: **내 기기 정보**(내 ID · 세션 비밀번호 + 재생성) + **원격 접속**(원격지 코드 · 접속 비밀번호 · 즉석 접속)
 *   서랍 메뉴: **계정**(로그인 → 팝업) · **설정**(→ 창 안 설정 화면: 원격 입력 제어 · 무인 접속 · 자동 실행 · 조직 — 2026-09-29)
 *
 * 종전에는 로그인 폼이 화면 가운데를 차지했다. 이 앱을 여는 사람의 대부분은 **지원을 받거나(내 ID 확인) 코드로 바로 접속**하려는
 * 사람이라 로그인은 서랍으로 옮겼다. 로그인 성공 시 `onLogin` 이 끝나면 상위(App)가 로그인 후 화면으로 바꾼다.
 * 이름(`LoginView`)·속성은 종전 그대로 둔다(App 변경 최소화).
 */
export function LoginView({
  onLogin,
  onAnonConnect,
  anonBusy,
  anonError,
  notice,
}: {
  /** 로그인(이메일 = 로그인 ID, §14.19). */
  onLogin: (email: string, password: string) => Promise<void>;
  onAnonConnect?: (deviceCode: string, password: string) => void;
  anonBusy?: boolean;
  anonError?: string;
  /** 로그인 화면으로 되돌아온 이유(예: 유효 시간 만료) — 로그인 팝업 위에 보인다. */
  notice?: string;
}) {
  // 기기 정보(카드)와 설정(서랍)이 **같은 상태**를 쓴다 — 한 번만 구독한다.
  const conn = useMyConnection();
  const [menuOpen, setMenuOpen] = useState(false);
  // 자격이 만료돼 되돌아왔으면 로그인 팝업을 바로 연다(무엇을 하라는지 말해 준다).
  const [loginOpen, setLoginOpen] = useState(!!notice);
  useEffect(() => {
    if (notice) setLoginOpen(true);
  }, [notice]);

  // 계정 없는 즉석 접속 입력(원격지 코드 + 접속 비밀번호).
  const [anonCode, setAnonCode] = useState(AUTO.anonCode ?? '');
  const [anonPw, setAnonPw] = useState(AUTO.anonPassword ?? '');
  const anonPwRef = useRef<HTMLInputElement>(null);

  // 이 PC 가 무인 원격지원 대상이고 이 창이 그 서비스에 붙어 있으면(껍데기 주입), 앞의 사람은 **지원을 받으려는 사람**이다
  // ⇒ 기기 정보가 주인공이고, 남을 제어하는 "원격 접속" 은 기본으로 접어 둔다(원하면 펼칠 수 있다 — 제거가 아니다).
  const unattendedHost = SHELL.unattendedHost === true;
  const [showConnect, setShowConnect] = useState(!unattendedHost);
  const hasDevice = !!conn.deviceCode || unattendedHost;

  // 창 안 설정 화면(≡ → 설정, 2026-09-29). 열려 있는 동안은 설정 화면이 창 높이를 내용에 맞춘다.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  // 메인 창 크기: 무인 대상 PC 에서 카드가 접혀 있으면 낮은 창, 펼치면(다른 PC 제어) 보통 창(2026-09-29).
  // 설정 화면을 닫으면 다시 불려 원래 크기로 돌아간다(Rust 는 설정 화면이 맞춘 뒤의 배치 요청을 무시하지 않는다).
  useEffect(() => {
    if (settingsOpen) return;
    applyMainLayout(unattendedHost && !showConnect ? MAIN_LAYOUT.COMPACT_HOST : MAIN_LAYOUT.COMPACT);
  }, [unattendedHost, showConnect, settingsOpen]);

  const submitAnon = () => anonCode && anonPw && onAnonConnect?.(anonCode, anonPw);

  return (
    <div className="home">
      <header className="home-top">
        {/* 제목을 누르면 메인 화면(카드)으로 — 설정 화면에서 돌아가는 또 하나의 길(2026-09-29 사용자 요청). */}
        <button className="home-brand" title="메인 화면으로" onClick={closeSettings}>
          RemoteSupport{SHELL.appVersion && <span className="home-ver">v{SHELL.appVersion}</span>}
        </button>
        <div className="home-top-actions">
          {/* [로그인] = 머리줄 오른쪽(2026-10-12 사용자 요청 — "메뉴(≡)에 들어가 누르기 불편").
              카드 안(원격지 코드·비밀번호 아래)에 두면 "입력한 코드로 로그인" 으로 읽혀 헷갈린다(사용자 지적) → 머리줄로. */}
          <button className="home-login" onClick={() => setLoginOpen(true)}>
            로그인
          </button>
          <button className="menu-btn" aria-label="메뉴" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {settingsOpen ? (
        <div className="home-scroll">
          <SettingsPage conn={conn} onBack={closeSettings} />
        </div>
      ) : (
      /* ⚠️ 가운데 정렬은 안쪽 래퍼의 margin:auto 로 — 바깥 justify-content:center 는 내용이 길 때 위가 잘린다(실측). */
      <div className="home-scroll">
        <div className="home-stack">
          {unattendedHost && (
            <div className="unattended-banner">
              이 PC 는 <b>무인 원격지원 대상</b>입니다. 아래 <b>내 ID</b> 와 <b>세션 비밀번호</b> 를 지원 담당자에게 알려 주세요.
              창을 닫아도 원격지원은 계속 가능합니다.
            </div>
          )}
          <div className="home-card">
            <MyDeviceInfo conn={conn} />
            {hasDevice && <hr className="home-divider" />}

            {unattendedHost && !showConnect ? (
              <button className="ghost" onClick={() => setShowConnect(true)}>
                다른 PC 를 제어하려면 여기를 누르세요
              </button>
            ) : (
              <>
              {onAnonConnect && (
                <section className="home-section">
                  <h2 className="home-h">원격 접속</h2>
                  <div className="field">
                    <label>원격지 코드</label>
                    {/* 최근 접속한 코드를 고를 수 있다(성공한 코드만 · 비밀번호는 저장하지 않는다 — recentCodes.ts). */}
                    <RecentCodeField
                      value={anonCode}
                      onChange={setAnonCode}
                      onPicked={() => anonPwRef.current?.focus()}
                      placeholder="123-456-789"
                    />
                  </div>
                  <div className="field">
                    <label>접속 비밀번호</label>
                    <PasswordField
                      inputRef={anonPwRef}
                      value={anonPw}
                      onChange={setAnonPw}
                      aria-label="접속 비밀번호"
                      style={{ width: '100%' }}
                      onKeyDown={(e) => e.key === 'Enter' && submitAnon()}
                    />
                  </div>
                  {anonError && <div className="hint" style={{ color: 'var(--err)' }}>{anonError}</div>}
                  <button className="ghost" onClick={submitAnon} disabled={anonBusy || !anonCode || !anonPw}>
                    {anonBusy ? '접속 중…' : '즉석 접속 (시간 제한)'}
                  </button>
                  <div className="hint" style={{ marginTop: 4 }}>
                    로그인 없이 접속합니다. 무료 사용 시간이 소진되면 세션이 자동 종료됩니다. 등록된 기기 목록은 오른쪽 위 [로그인] 후
                    볼 수 있습니다.
                  </div>
                </section>
              )}
              </>
            )}
          </div>
        </div>
      </div>
      )}

      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} title="메뉴">
        <div className="drawer-section">
          <div className="drawer-h">계정</div>
          <button
            className="drawer-cta"
            onClick={() => {
              setMenuOpen(false);
              setLoginOpen(true);
            }}
          >
            로그인
          </button>
        </div>
        {/* 설정은 **창 안 설정 화면**(2026-09-29 사용자 결정) — 서랍에 다 넣으면 작은 창에서 위아래가 잘려 스크롤이 많았다.
            설정 화면은 열려 있는 동안 창 높이를 내용에 맞춘다(SettingsPage). */}
        {IS_SHELL && conn.deviceCode && (
          <div className="drawer-section">
            <div className="drawer-h">설정</div>
            <button
              className="drawer-item"
              onClick={() => {
                setMenuOpen(false);
                setSettingsOpen(true);
              }}
            >
              내 기기 설정
              <span className="drawer-item-sub">원격 입력 제어 · 무인 접속 · 조직 등록</span>
            </button>
          </div>
        )}
      </Drawer>

      {loginOpen && <LoginDialog notice={notice} onLogin={onLogin} onClose={() => setLoginOpen(false)} />}
    </div>
  );
}

/**
 * 로그인 팝업 — 이메일 + 비밀번호(§14.19 — 이메일 = 로그인 ID) · 서버 변경. Esc·바깥 누름 = 닫기.
 * 마지막으로 로그인에 성공한 이메일을 채우고, 채워졌으면 비밀번호 칸에 포커스(2026-09-27).
 * 종전의 "조직 선택"(같은 ID 가 여러 조직에 있을 때 409)은 이메일이 전역 유일해지면서 없어졌다.
 */
function LoginDialog({
  notice,
  onLogin,
  onClose,
}: {
  notice?: string;
  onLogin: (email: string, password: string) => Promise<void>;
  onClose: () => void;
}) {
  const [initialEmail] = useState(() => AUTO.loginId || loadLastLoginId());
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(AUTO.password);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editServer, setEditServer] = useState(false);
  const [serverUrl, setServerUrl] = useState(SERVER);
  // 통합앱에서 서버를 바꾼 뒤 "다시 시작해야 적용됩니다" 안내.
  const [serverSaved, setServerSaved] = useState(false);
  // "비밀번호를 잊으셨나요?" — 같은 팝업 안에서 찾기 화면으로 바꾼다.
  const [forgotOpen, setForgotOpen] = useState(false);

  const saveServer = async () => {
    await setServer(serverUrl.trim());
    // 통합앱은 **피제어 에이전트가 스폰 시점에 서버를 확정**하므로 새로고침만으로는 부족하다 → 다시 시작 안내.
    if (IS_SHELL) {
      setServerSaved(true);
      return;
    }
    location.reload(); // 서버 주소는 로드 시 확정된다
  };

  if (forgotOpen) {
    return <ForgotPasswordDialog initialEmail={email} onBack={() => setForgotOpen(false)} onClose={onClose} />;
  }

  /** 로그인 시도. 실패 문구는 서버가 준 것(없는 이메일·틀린 비밀번호가 같은 문구 — 계정 열거 방지). */
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await onLogin(email.trim(), password);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="modal-box login-box" role="dialog" aria-modal="true" aria-labelledby="login-title">
        <div className="modal-head">
          <h2 id="login-title">로그인</h2>
          <button className="modal-x" aria-label="닫기" onClick={onClose}>
            ✕
          </button>
        </div>
        {notice && <div className="conn-warn">{notice}</div>}
        <div className="field">
          <label>이메일</label>
          {/* 전에 로그인한 이메일 · `@` 뒤 도메인 자동완성(2026-09-28, loginEmails.ts). 고르면 비밀번호 칸으로. */}
          <EmailSuggestField
            value={email}
            onChange={setEmail}
            onPicked={() => passwordRef.current?.focus()}
            autoFocus={!initialEmail}
          />
        </div>
        <div className="field">
          <label>비밀번호</label>
          <PasswordField
            inputRef={passwordRef}
            value={password}
            onChange={setPassword}
            autoFocus={!!initialEmail}
            aria-label="로그인 비밀번호"
            style={{ width: '100%' }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </div>
        {error && <div className="hint" style={{ color: 'var(--err)' }}>{error}</div>}

        <button onClick={() => submit()} disabled={busy || !email.trim() || !password}>
          {busy ? '로그인 중…' : '로그인'}
        </button>
        {/* 비밀번호 찾기(§14.19.9) — 메일로 재설정 링크. 초대 메일을 놓친 사람도 이 경로로 비밀번호를 정할 수 있다. */}
        <button className="linklike" style={{ alignSelf: 'flex-start', marginTop: 8 }} onClick={() => setForgotOpen(true)}>
          비밀번호를 잊으셨나요?
        </button>

        {/* 서버 설정 — 서버 주소 지정/변경(없으면 same-origin). */}
        <div className="hint" style={{ marginTop: 8 }}>
          {editServer ? (
            <div className="row">
              <input
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="http://서버:3000 (비우면 이 서버)"
                style={{ flex: 1 }}
              />
              <button className="ghost" onClick={saveServer}>
                저장
              </button>
            </div>
          ) : (
            <span>
              서버: {SERVER || '이 서버(same-origin)'}{' '}
              <button className="linklike" onClick={() => setEditServer(true)}>
                변경
              </button>
            </span>
          )}
          {serverSaved && (
            <div style={{ marginTop: 4, color: '#ffc107' }}>
              저장했습니다. <b>앱을 다시 시작하면</b> 적용됩니다.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * "비밀번호 찾기"(§14.19.9) — 이메일을 받아 재설정 링크 메일을 요청한다.
 * 서버는 계정이 있든 없든 같은 응답을 준다(계정 열거 방지) → 화면도 늘 **같은 안내**("등록돼 있으면 보냈습니다")를 보인다.
 * 링크는 브라우저에서 열리는 설정 페이지로 간다 — 설정한 뒤 이 창에서 로그인한다.
 */
function ForgotPasswordDialog({
  initialEmail,
  onBack,
  onClose,
}: {
  initialEmail: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await requestPasswordReset(email.trim());
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="modal-box login-box" role="dialog" aria-modal="true" aria-labelledby="forgot-title">
        <div className="modal-head">
          <h2 id="forgot-title">비밀번호 찾기</h2>
          <button className="modal-x" aria-label="닫기" onClick={onClose}>
            ✕
          </button>
        </div>
        {done ? (
          <div className="hint" role="status" style={{ lineHeight: 1.6 }}>
            <b>{email.trim()}</b> 이(가) 등록된 이메일이면 비밀번호 재설정 링크를 보냈습니다.
            <br />
            메일함(스팸함 포함)을 확인하고 링크에서 새 비밀번호를 정한 뒤 로그인하세요. 링크는 1시간 동안 유효합니다.
          </div>
        ) : (
          <>
            <div className="hint" style={{ marginBottom: 8 }}>
              로그인 이메일을 입력하면 비밀번호를 새로 정할 수 있는 링크를 메일로 보내 드립니다.
            </div>
            <div className="field">
              <label>이메일</label>
              <input
                type="email"
                inputMode="email"
                autoComplete="off"
                aria-label="찾을 이메일"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && email.trim() && !busy && void submit()}
                autoFocus
              />
            </div>
            {error && <div className="hint" style={{ color: 'var(--err)' }}>{error}</div>}
            <button onClick={() => void submit()} disabled={busy || !email.trim()}>
              {busy ? '보내는 중…' : '재설정 메일 보내기'}
            </button>
          </>
        )}
        <button className="linklike" style={{ alignSelf: 'flex-start', marginTop: 10 }} onClick={onBack}>
          ← 로그인으로 돌아가기
        </button>
      </div>
    </div>
  );
}
